import {analytics} from '@cl/shared';
import {CLEnumKeys, CLEnumsMapper} from 'common/src/modules/enums/enums.types';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _keys from 'lodash/keys';
import _map from 'lodash/map';
import _mapValues from 'lodash/mapValues';
import _orderBy from 'lodash/orderBy';
import _reduce from 'lodash/reduce';
import _reject from 'lodash/reject';
import _split from 'lodash/split';
import {DateTime} from 'luxon';
import {
  AnalyticsEventType,
  AnalyticsFilterItem,
  AnalyticsTotalsWithTrends,
  CampaignMetric,
  DimensionValue,
  InsightsMetricSavedViewsOption,
  InsightsMetricSelectOption,
  MetricsChartType,
  MetricsGroupBy,
  MetricValue,
  RemoteAnalyticsTotalsWithTrends,
  RemoteCampaignMetric,
  RemoteInsightsMetricSavedViewsOption,
  RemoteOptionalDimensions,
  RemoteOptionalMetrics,
} from './analytics.types';
import convertOptionalValues from './utils/convertOptionalValues';
import getAllSupportedMetrics from './utils/getAllSupportedMetrics';
import getAnalyticsUIName from './utils/getAnalyticsUIName';

const {cachedAnalyticsSubject} = analytics;

function toClientOptionalDimensions(
  data: RemoteOptionalDimensions[],
  customMetricsOptions?: Partial<Record<MetricValue, string[]>>,
): InsightsMetricSelectOption<DimensionValue>[] {
  const allowedDimensions = _filter(data, ({value}) => !!getAnalyticsUIName(value));

  const dimensions = _map(
    _reject(allowedDimensions, {uiName: DimensionValue.UserStateShort}),
    (dimension) => {
      return {
        ...dimension,
        uiName: dimension.label ?? getAnalyticsUIName(dimension.value),
        supportedMetrics:
          getAllSupportedMetrics(dimension.supportedMetrics, customMetricsOptions ?? {}) ?? [],
        unsupportedDimensions: dimension?.unsupportedDimensions ?? [],
      };
    },
  );

  return _orderBy(convertOptionalValues(dimensions), ['label'], ['asc']);
}

function toClientOptionalDimensionsForFilters(
  data: RemoteOptionalDimensions[],
): InsightsMetricSelectOption<DimensionValue>[] {
  const customMetricsOptions = {
    [MetricValue.CustomConversions]: _map(
      cachedAnalyticsSubject.data.optionsByMetricsKey[MetricValue.CustomConversions],
      'value',
    ),
  };

  const optionalFiltersWithCustomMetrics = toClientOptionalDimensions(data, customMetricsOptions);

  return _orderBy(optionalFiltersWithCustomMetrics, ['label'], ['asc']);
}

function toClientOptionalMetrics({
  data,
  hasMetaCustomConversions,
  customMetricsOptions,
}: {
  data: RemoteOptionalMetrics[];
  hasMetaCustomConversions?: boolean;
  customMetricsOptions?: Partial<Record<MetricValue, string[]>>;
}): InsightsMetricSelectOption<MetricValue>[] {
  const {unsupportedDimensions = [], supportedMetrics = []} = _find(data, {
    value: MetricValue.Conversions,
  }) ?? {unsupportedDimensions: [], supportedMetrics: []};

  const allowedMetrics = _filter(data, ({value}) => !!getAnalyticsUIName(value));
  const metrics = _map(allowedMetrics, (metric) => {
    return {
      ...metric,
      uiName: getAnalyticsUIName(metric.value),
      supportedMetrics: getAllSupportedMetrics(metric.supportedMetrics, customMetricsOptions ?? {}),
      unsupportedDimensions: metric.unsupportedDimensions ?? [],
    };
  });

  return _orderBy(
    [
      ...convertOptionalValues(metrics),
      ...(customMetricsOptions
        ? [
            {
              label: getAnalyticsUIName(MetricValue.CustomConversions),
              value: MetricValue.CustomConversions,
              eventType: AnalyticsEventType.Adserving,
              unsupportedDimensions,
              supportedMetrics: [
                ...supportedMetrics,
                ...(customMetricsOptions[MetricValue.CustomConversions] as MetricValue[]),
              ],
            },
          ]
        : []),
      ...(hasMetaCustomConversions
        ? [
            {
              label: getAnalyticsUIName(MetricValue.CustomMetaConversions),
              value: MetricValue.CustomMetaConversions,
              unsupportedDimensions,
              eventType: AnalyticsEventType.Adserving,
              supportedMetrics: [
                ...supportedMetrics,
                ...(customMetricsOptions?.[MetricValue.CustomConversions] as MetricValue[]),
              ],
            },
          ]
        : []),
    ],
    ['label'],
    ['asc'],
  );
}

function toClientSavedViewData(
  data: RemoteInsightsMetricSavedViewsOption[],
): InsightsMetricSavedViewsOption[] {
  return _map(data, (item) => {
    const {
      filters,
      fromInclusive,
      graphStyle,
      metrics,
      toExclusive,
      viewBy,
      viewName,
      id,
      customConversions,
      metricsGroupBy,
      eventType,
    } = item;

    return {
      value: `${id}`,
      label: viewName,
      params: {
        metrics: [...(metrics ?? []), ...(customConversions ?? [])],
        dimensions: viewBy,
        dateRange: {
          startDate: DateTime.fromISO(fromInclusive),
          endDate: DateTime.fromISO(toExclusive),
        },
        filters: filters
          ? _map(_keys(filters), (item: DimensionValue) => {
              return {
                allValues: _split(filters[item], ','),
                filterBy: item,
              };
            })
          : [],
        graphStyle: graphStyle.toLowerCase() as MetricsChartType,
        groupBy: metricsGroupBy.toLocaleLowerCase() as MetricsGroupBy,
        eventType: eventType ?? AnalyticsEventType.Adserving,
      },
    };
  });
}

function toClientDimensionPossibleValues(data: Record<string, DimensionValue>) {
  return _reduce(
    data,
    (acc, value, key) => ({
      ...acc,
      [value]: {
        value,
        uiName: key,
      },
    }),
    {},
  );
}

function toClientOptionsForDimensionValue(campaignEnums: Record<string, CLEnumsMapper>) {
  return _mapValues(
    campaignEnums[CLEnumKeys.CampaignPage.CampaignType].byName,
    ({uiName, name}) => ({
      uiName,
      value: name,
    }),
  );
}

function toClientCampaignsMetric({
  caid: campaignId,
  imp: impressions,
  clk: clicks,
  ctr,
  eng: engagements,
  ctc,
  vtc,
  cr,
  sales,
  viewability,
  qt1: firstQuarter,
  mid: secondQuarter,
  qt3: thirdQuarter,
  comp: videoComplete,
  skip,
  vcr,
  conv,
  ctcr,
  vtcr,
  view_y: viewedImpressions,
}: RemoteCampaignMetric): CampaignMetric {
  return {
    campaignId,
    impressions,
    clicks,
    ctr: ctr * 100,
    engagements,
    ctc,
    vtc,
    cr: cr * 100,
    sales,
    viewability: viewability * 100,
    firstQuarter,
    secondQuarter,
    thirdQuarter,
    videoComplete,
    skip,
    vcr: vcr * 100,
    conv,
    ctcr: ctcr * 100,
    vtcr: vtcr * 100,
    viewedImpressions,
  };
}

function toRemoteFilters(filters: AnalyticsFilterItem[]) {
  return _reduce(
    filters,
    (acc, {filterBy, allValues}) => {
      return {...acc, [filterBy]: allValues};
    },
    {},
  );
}

function toClientAnalyticsTotalsWithTrends(
  remoteData: RemoteAnalyticsTotalsWithTrends,
): AnalyticsTotalsWithTrends {
  return _reduce(
    remoteData,
    (
      acc,
      {
        fromInclusiveFirst,
        toExclusiveFirst,
        fromInclusiveSecond,
        toExclusiveSecond,
        percent,
        total,
      },
      metric: MetricValue,
    ) => {
      return {
        ...acc,
        [metric]: {
          fromStartDate: DateTime.fromISO(fromInclusiveFirst),
          toStartDate: DateTime.fromISO(toExclusiveFirst).minus({days: 1}),
          fromEndDate: DateTime.fromISO(fromInclusiveSecond),
          toEndDate: DateTime.fromISO(toExclusiveSecond),
          percent: +percent,
          total: total,
        },
      };
    },
    {},
  ) as AnalyticsTotalsWithTrends;
}

function toClientOptionalInsightsDimensions(
  data: RemoteOptionalDimensions[],
): InsightsMetricSelectOption<DimensionValue>[] {
  const allowedDimensions = _filter(data, ({value}) => !!getAnalyticsUIName(value));

  const dimensions = _map(
    _reject(allowedDimensions, {uiName: DimensionValue.UserStateShort}),
    (dimension) => {
      return {
        ...dimension,
        uiName: getAnalyticsUIName(dimension.value),
        supportedMetrics: [],
        unsupportedDimensions: [],
      };
    },
  );

  return _orderBy(convertOptionalValues(dimensions), ['label'], ['asc']);
}

export {
  toClientAnalyticsTotalsWithTrends,
  toClientCampaignsMetric,
  toClientDimensionPossibleValues,
  toClientOptionalDimensions,
  toClientOptionalDimensionsForFilters,
  toClientOptionalInsightsDimensions,
  toClientOptionalMetrics,
  toClientOptionsForDimensionValue,
  toClientSavedViewData,
  toRemoteFilters,
};
